import {
  Box,
  Chip,
  IconButton,
  Popover,
  Select,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { FilterList as FilterListIcon, Close as CloseIcon, Add as AddIcon } from '@mui/icons-material';
import React, { useState } from 'react';
import { Button } from '../../MUI/button/Button';
import { FilterField, FilterRow, FilterState, FilterBuilderProps } from './filterTypes';

export const FilterBuilder: React.FC<FilterBuilderProps> = ({ fields, onApply, onClear, activeFilters = 0 }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState<FilterState>({ rows: [], operator: 'AND' });

  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAddRow = () => {
    const firstField = fields[0];
    setDraft(prev => ({
      ...prev,
      rows: [
        ...prev.rows,
        {
          id: `filter-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          field: firstField?.key ?? '',
          operator: firstField?.operators[0]?.value ?? '',
          value: '',
        },
      ],
    }));
  };

  const handleRemoveRow = (id: string) => {
    setDraft(prev => ({
      ...prev,
      rows: prev.rows.filter(r => r.id !== id),
    }));
  };

  const handleFieldChange = (id: string, fieldKey: string) => {
    const field = fields.find(f => f.key === fieldKey);
    setDraft(prev => ({
      ...prev,
      rows: prev.rows.map(r =>
        r.id === id
          ? { id: r.id, field: fieldKey, operator: field?.operators[0]?.value ?? '', value: '' }
          : r
      ),
    }));
  };

  const handleOperatorChange = (id: string, op: string) => {
    setDraft(prev => ({
      ...prev,
      rows: prev.rows.map(r => (r.id === id ? { ...r, operator: op, value: '' } : r)),
    }));
  };

  const handleValueChange = (id: string, val: string | boolean) => {
    setDraft(prev => ({
      ...prev,
      rows: prev.rows.map(r => (r.id === id ? { ...r, value: val } : r)),
    }));
  };

  const handleOperatorToggle = (_: React.MouseEvent<HTMLElement>, newOp: 'AND' | 'OR' | null) => {
    if (newOp) setDraft(prev => ({ ...prev, operator: newOp }));
  };

  const handleApply = () => {
    onApply(draft);
    handleClose();
  };

  const handleClear = () => {
    setDraft({ rows: [], operator: 'AND' });
    onClear();
    handleClose();
  };

  const getField = (key: string): FilterField | undefined => fields.find(f => f.key === key);

  return (
    <>
      <Box display='flex' alignItems='center' gap={1}>
        <Button
          variant={activeFilters > 0 ? 'contained' : 'outlined'}
          color='primary'
          size='small'
          startIcon={<FilterListIcon />}
          onClick={handleOpen}
        >
          Filtros{activeFilters > 0 ? ` (${activeFilters})` : ''}
        </Button>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, width: 480, maxHeight: 420 } }}
      >
        {/* Header */}
        <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
          <Typography variant='subtitle1' fontWeight={600}>Filtros</Typography>
          <Button variant='text' size='small' color='error' onClick={handleClear}>
            Limpar
          </Button>
        </Box>

        {/* Filter rows */}
        <Box display='flex' flexDirection='column' gap={1.5} mb={2}>
          {draft.rows.length === 0 && (
            <Typography variant='body2' color='text.secondary' textAlign='center' py={2}>
              Nenhum filtro aplicado. Clique em &quot;Adicionar filtro&quot; para começar.
            </Typography>
          )}

          {draft.rows.map(row => {
            const field = getField(row.field);
            return (
              <Box key={row.id} display='flex' gap={1} alignItems='center'>
                {/* Field select */}
                <Select
                  size='small'
                  value={row.field}
                  onChange={e => handleFieldChange(row.id, e.target.value)}
                  sx={{ minWidth: 150 }}
                >
                  {fields.map(f => (
                    <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>
                  ))}
                </Select>

                {/* Operator select */}
                {field && (
                  <Select
                    size='small'
                    value={row.operator}
                    onChange={e => handleOperatorChange(row.id, e.target.value)}
                    sx={{ minWidth: 110 }}
                  >
                    {field.operators.map(op => (
                      <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>
                    ))}
                  </Select>
                )}

                {/* Value input */}
                {field?.valueType === 'number' && (
                  <TextField
                    size='small'
                    type='number'
                    value={typeof row.value === 'number' ? row.value : row.value}
                    onChange={e => handleValueChange(row.id, e.target.value)}
                    sx={{ width: 80 }}
                    inputProps={{ min: 0 }}
                  />
                )}

                {field?.valueType === 'boolean' && (
                  <Select
                    size='small'
                    value={typeof row.value === 'boolean' ? (row.value ? 'true' : 'false') : ''}
                    onChange={e => handleValueChange(row.id, e.target.value === 'true')}
                    sx={{ minWidth: 80 }}
                  >
                    {field.booleanOptions?.map(opt => (
                      <MenuItem key={String(opt.value)} value={String(opt.value)}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                )}

                {/* Suffix */}
                {field?.suffix && (
                  <Typography variant='body2' color='text.secondary' sx={{ minWidth: 40 }}>
                    {field.suffix}
                  </Typography>
                )}

                {/* Remove button */}
                <IconButton size='small' onClick={() => handleRemoveRow(row.id)} color='error'>
                  <CloseIcon fontSize='small' />
                </IconButton>
              </Box>
            );
          })}
        </Box>

        {/* Add filter button */}
        <Button
          variant='text'
          size='small'
          startIcon={<AddIcon />}
          onClick={handleAddRow}
          sx={{ mb: 1.5 }}
        >
          Adicionar filtro
        </Button>

        {/* AND / OR toggle + Apply */}
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <ToggleButtonGroup
            value={draft.operator}
            exclusive
            onChange={handleOperatorToggle}
            size='small'
          >
            <ToggleButton value='AND' sx={{ px: 2 }}>E</ToggleButton>
            <ToggleButton value='OR' sx={{ px: 2 }}>OU</ToggleButton>
          </ToggleButtonGroup>

          <Button variant='contained' size='small' onClick={handleApply}>
            Aplicar
          </Button>
        </Box>
      </Popover>
    </>
  );
};

/** Active filter chips to display below the header */
interface FilterChipsProps {
  rows: FilterRow[];
  fields: FilterField[];
  onRemove: (id: string) => void;
  onClear: () => void;
}

export const FilterChips: React.FC<FilterChipsProps> = ({ rows, fields, onRemove, onClear }) => {
  const getField = (key: string): FilterField | undefined => fields.find(f => f.key === key);

  const getRowLabel = (row: FilterRow): string => {
    const field = getField(row.field);
    if (!field) return row.field;
    const op = field.operators.find(o => o.value === row.operator);
    const val = field.valueType === 'boolean'
      ? (row.value === true || row.value === 'true' ? 'Sim' : 'Não')
      : row.value;
    return `${field.label}: ${op?.label ?? row.operator} ${val}${field.suffix ? ` ${field.suffix}` : ''}`;
  };

  if (rows.length === 0) return null;

  return (
    <Box display='flex' gap={0.5} flexWrap='wrap' alignItems='center'>
      {rows.map(row => (
        <Chip
          key={row.id}
          label={getRowLabel(row)}
          size='small'
          onDelete={() => onRemove(row.id)}
          color='primary'
          variant='outlined'
        />
      ))}
      <Chip
        label='Limpar filtros'
        size='small'
        onDelete={onClear}
        variant='outlined'
        sx={{ opacity: 0.6, cursor: 'pointer', borderStyle: 'dashed', '& .MuiChip-deleteIcon': { display: 'none' } }}
        onClick={onClear}
      />
    </Box>
  );
};
